import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements, PaymentRequestButton } from '@stripe/react-stripe-js';
import { FaCheckCircle, FaTimesCircle, FaCreditCard } from 'react-icons/fa';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY, {
  betas: ['payment_request_button_beta_1']
});

if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
  console.error('Stripe publishable key is missing. Make sure to set REACT_APP_STRIPE_PUBLISHABLE_KEY in your environment variables.');
}

const PaymentForm = ({ amount, vendorDetails, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);

  useEffect(() => {
    if (!stripe || !elements) return;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: vendorDetails?.name || 'Total',
        amount: Math.round(parseFloat(amount) * 100), 
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestShipping: false,
    //   disableWallets: ['applePay'], 
    });

    // Check if the Payment Request is available
    (async () => {
      const result = await pr.canMakePayment();
      //console.log('Payment Request availability:', result);
      if (result && result.googlePay) {
        setPaymentRequest(pr);
      }
    })();

    pr.on('paymentmethod', async (ev) => {
      setProcessing(true);
      try {
        const response = await fetch('https://valley-tickets-api-7d6b7110f95c.herokuapp.com/api/confirm-payment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            paymentMethodId: ev.paymentMethod.id,
            amount: amount,
          }),
        });

        const result = await response.json();

        if (result.error) {
          ev.complete('fail');
          setError(result.error);
        } else {
          ev.complete('success');
          onSuccess();
        }
      } catch (err) {
        ev.complete('fail');
        setError('An unexpected error occurred');
      } finally {
        setProcessing(false);
      }
    });
  }, [stripe, elements, amount, vendorDetails]);

  const handleCardSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (stripeError) {
        setError(stripeError.message);
        return;
      }

      const response = await fetch('https://valley-tickets-api-7d6b7110f95c.herokuapp.com/api/confirm-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          amount: amount,
        }),
      });

      const result = await response.json();

      if (result.error) {
        setError(result.error);
      } else {
        onSuccess();
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      {paymentRequest && (
        <div className="space-y-4">
          <PaymentRequestButton
            paymentRequest={paymentRequest}
            className="w-full"
            style={{
              paymentRequestButton: {
                type: 'pay',
                theme: 'dark',
                height: '48px',
              },
            }}
          />
        </div>
      )}

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">Or pay with card</span>
        </div>
      </div>

      <form onSubmit={handleCardSubmit} className="space-y-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </div>
        {error && (
          <div className="text-red-500 text-sm">{error}</div>
        )}
        <button
          type="submit"
          disabled={!stripe || processing}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
        >
          <FaCreditCard className="mr-2" />
          {processing ? 'Processing...' : 'Pay with Card'}
        </button>
      </form>
    </div>
  );
};

const PaymentCompletion = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [vendorDetails, setVendorDetails] = useState(null);
  const [amount, setAmount] = useState((0).toFixed(2));
  const [amountLoaded, setAmountLoaded] = useState(false);
  const [stripeError, setStripeError] = useState(null);

  useEffect(() => {
    stripePromise.then(
      (stripe) => {
        if (!stripe) {
          setStripeError('Failed to load Stripe. Please try again later.');
        }
      },
      (error) => {
        console.error('Stripe loading error:', error);
        setStripeError('Failed to load payment system. Please try again later.');
      }
    );
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentId = params.get('paymentId');
    const vendorId = params.get('vendorId');

    if (!paymentId || !vendorId) {
      navigate('/');
      return;
    }

    const fetchPaymentDetails = async () => {
      try {
        setAmountLoaded(false);
        const response = await fetch(`https://valley-tickets-api-7d6b7110f95c.herokuapp.com/api/payment-details/${paymentId}`);
        const data = await response.json();
        setVendorDetails(data.vendorDetails);
        setAmount(data.amount);
        setAmountLoaded(true);
      } catch (error) {
        console.error('Error fetching payment details:', error);
      }
    };

    fetchPaymentDetails();

  
    const ws = new WebSocket(`ws://valley-tickets-api-7d6b7110f95c.herokuapp.com//api/payment-status?paymentId=${paymentId}`);
    
    ws.onmessage = (event) => {
      const { status } = JSON.parse(event.data);
      setPaymentStatus(status);
      if (status === 'succeeded') {
        setTimeout(() => {
          window.close();
        }, 2000);
      }
    };

    return () => ws.close();
  }, [location, navigate]);

  const handlePaymentSuccess = () => {
    setPaymentStatus('succeeded');
    setTimeout(() => {
      window.close();
    }, 2000);
  };

  if (stripeError) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <FaTimesCircle className="text-red-500 text-6xl mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Payment System Error</h2>
          <p className="text-gray-600">{stripeError}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'succeeded') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <FaCheckCircle className="text-green-500 text-6xl mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
          <p className="text-gray-600">Your payment has been processed successfully.</p>
          <p className="text-sm text-gray-500 mt-4">This window will close automatically...</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'failed') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <FaTimesCircle className="text-red-500 text-6xl mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Payment Failed</h2>
          <p className="text-gray-600">There was an error processing your payment.</p>
          <button
            onClick={() => window.close()}
            className="mt-4 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
        <h1 className="text-2xl font-bold mb-6">Complete Your Payment</h1>
        
        {vendorDetails && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-2">{vendorDetails.name}</h2>
            <p className="text-gray-600">{vendorDetails.description}</p>
          </div>
        )}

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">Amount to Pay</h3>
          {amountLoaded ? (
            <p className="text-3xl font-bold">${amount}</p>
          ) : (
            <p className="text-3xl font-bold">Loading...</p>
          )}
        </div>

        <Elements stripe={stripePromise}>
          <PaymentForm
            amount={amount}
            vendorDetails={vendorDetails}
            onSuccess={handlePaymentSuccess}
          />
        </Elements>
      </div>
    </div>
  );
};

export default PaymentCompletion; 